import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Patterns emitted by browsers (and the Angular router) when a lazily-loaded
 * ES module chunk cannot be fetched or parsed. This happens after a new
 * deploy replaces the hashed chunk files while a client still holds an old
 * index.html that references the now-missing chunks. Depending on the browser
 * and the server's fallback behaviour the failure surfaces as a failed dynamic
 * import, a generic module-script failure, or a MIME-type mismatch (the server
 * serves the SPA's index.html — text/html — in place of the missing .js file).
 */
const CHUNK_LOAD_ERROR_PATTERNS: readonly RegExp[] = [
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
    /Loading chunk \S+ failed/i,
    /ChunkLoadError/i,
    /is not a valid JavaScript MIME type/i
];

/**
 * sessionStorage key holding the timestamp of the last recovery reload. It is
 * used to guarantee we only force a single reload per short window, so a chunk
 * that is genuinely gone (and keeps failing after a refresh) can never trap the
 * user in an endless reload loop.
 */
const RELOAD_GUARD_KEY = 'mdd:chunk-load-reload-at';

/** Minimum time that must pass before a second recovery reload is allowed. */
const RELOAD_GUARD_WINDOW_MS = 30_000;

/**
 * Max time to wait for the Sentry event to flush before forcing the recovery
 * reload. `Sentry.captureException` queues the event asynchronously, so
 * reloading immediately after calling it can tear down the page before the
 * request is even sent — especially on browsers (e.g. Firefox, older Safari)
 * that don't support `fetch(..., { keepalive: true })`. Flushing first gives
 * the event a real chance to reach Sentry without noticeably delaying
 * recovery for the user.
 */
const SENTRY_FLUSH_TIMEOUT_MS = 2_000;

/**
 * ErrorHandler that keeps reporting to Sentry (so we retain visibility) but
 * additionally recovers from post-deploy chunk-load failures by forcing one
 * controlled reload. Reloading pulls a fresh index.html that references the
 * current chunk hashes, which resolves the mismatch instead of leaving the user
 * stranded on a broken page.
 */
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
    private readonly sentryErrorHandler = Sentry.createErrorHandler();

    handleError(error: unknown): void {
        this.sentryErrorHandler.handleError(error);

        if (isChunkLoadError(error)) {
            this.recoverByReloading();
        }
    }

    private recoverByReloading(): void {
        const storage = this.getSessionStorage();

        // Without a working sessionStorage we cannot guard against a reload
        // loop, so fail closed and skip the recovery reload entirely rather
        // than risk trapping the user in repeated unguarded reloads.
        if (!storage || this.hasRecentlyReloaded(storage) || !this.markReloaded(storage)) {
            return;
        }

        // Give the just-captured Sentry event a chance to actually be sent
        // before we tear down the page with a reload.
        Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS)
            .catch(() => undefined)
            .then(() => this.reloadPage());
    }

    private hasRecentlyReloaded(storage: Storage): boolean {
        let lastReloadAt: number;

        try {
            lastReloadAt = Number(storage.getItem(RELOAD_GUARD_KEY));
        } catch {
            // Reading the guard can throw under some storage-blocking
            // policies even when the object itself was accessible. We can't
            // tell whether we recently reloaded, so fail closed.
            return true;
        }

        if (!Number.isFinite(lastReloadAt) || lastReloadAt <= 0) {
            return false;
        }

        return Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS;
    }

    /** Persists the reload guard marker. Returns whether it was written. */
    private markReloaded(storage: Storage): boolean {
        try {
            storage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
            return true;
        } catch {
            // e.g. quota exceeded or storage blocked for writes. Without a
            // persisted marker we can't guard the next failure, so skip this
            // reload rather than risk an unguarded loop.
            return false;
        }
    }

    private getSessionStorage(): Storage | null {
        try {
            return typeof window !== 'undefined' ? window.sessionStorage : null;
        } catch {
            // Access to sessionStorage can throw (e.g. privacy modes). Without it
            // we cannot guard against loops, so we skip the recovery reload.
            return null;
        }
    }

    protected reloadPage(): void {
        window.location.reload();
    }
}

/**
 * Determines whether an error thrown into the global ErrorHandler represents a
 * failed lazy-chunk load. Angular wraps the underlying error in a number of
 * ways (unhandled rejections, re-thrown errors, plain strings), so we inspect
 * every message-bearing field we can reach.
 */
export function isChunkLoadError(error: unknown): boolean {
    const message = collectErrorText(error);

    if (!message) {
        return false;
    }

    return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function collectErrorText(error: unknown, depth = 0): string {
    if (error == null || depth > 3) {
        return '';
    }

    if (typeof error === 'string') {
        return error;
    }

    if (error instanceof Error) {
        return [error.name, error.message, collectNested(error, depth)]
            .filter(Boolean)
            .join(' ');
    }

    if (typeof error === 'object') {
        const record = error as Record<string, unknown>;
        const message = typeof record['message'] === 'string' ? record['message'] : '';

        return [message, collectNested(record, depth)].filter(Boolean).join(' ');
    }

    return '';
}

function collectNested(source: Record<string, unknown> | Error, depth: number): string {
    const record = source as Record<string, unknown>;

    return ['rejection', 'ngOriginalError', 'originalError', 'error', 'cause']
        .map((key) => collectErrorText(record[key], depth + 1))
        .filter(Boolean)
        .join(' ');
}
